import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { IftaLabelModule } from 'primeng/iftalabel';
import { CustomerItem } from '../customer-item.model';
import { CustomerItemSearchParams, CustomerItemService } from '../customer-item.service';
import { Page } from '../../../models/page-wrapper.model';
import { CustomersService } from '../../customers/customers.service';
import { Customer } from '../../customers/customers.model';
import { CustomerPickerSummary } from '../../customers/customers.model';
import { ItemService } from '../../items/item.service';
import { Item } from '../../items/item.model';
import { BrandsService } from '../../brands/brands.service';
import { Brand } from '../../brands/brands.model';
import { SubBrand } from '../../brands/sub-brands/sub-brands.model';
import { SubBrandsService } from '../../brands/sub-brands/sub-brands.service';
import { EntityDropdownPickerComponent } from '../../../shared/components/entity-dropdown-picker/entity-dropdown-picker.component';
import { EntityDropdownPickerAdapter } from '../../../shared/components/entity-dropdown-picker/entity-dropdown-picker.model';

@Component({
  selector: 'app-list-customer-item',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    TableModule,
    ButtonModule,
    ConfirmDialogModule,
    ToastModule,
    InputTextModule,
    DropdownModule,
    InputGroupModule,
    InputGroupAddonModule,
    IftaLabelModule,
    EntityDropdownPickerComponent,
  ],
  providers: [ConfirmationService, MessageService],
  templateUrl: './list-customer-item.component.html',
  styleUrl: './list-customer-item.component.css',
})
export class ListCustomerItemComponent implements OnInit {
  @ViewChild('dt') dt!: Table;

  customerItems: CustomerItem[] = [];
  totalRecords = 0;
  isLoading = false;
  rows = 25;
  filterForm: FormGroup;
  customerPickerAdapter: EntityDropdownPickerAdapter<CustomerPickerSummary, Customer>;
  itemPickerAdapter: EntityDropdownPickerAdapter<Item>;
  brandPickerAdapter: EntityDropdownPickerAdapter<Brand>;
  subBrandPickerAdapter: EntityDropdownPickerAdapter<SubBrand>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private customerItemService: CustomerItemService,
    private customersService: CustomersService,
    private itemService: ItemService,
    private brandsService: BrandsService,
    private subBrandsService: SubBrandsService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService
  ) {
    this.customerPickerAdapter = this.customersService.createCustomerPickerAdapter();
    this.itemPickerAdapter = this.itemService.createItemPickerAdapter();
    this.brandPickerAdapter = this.brandsService.createBrandPickerAdapter();
    this.subBrandPickerAdapter = this.subBrandsService.createSubBrandPickerAdapter();
    this.filterForm = this.fb.group({
      customerItemNumber: [''],
      customerItemDescription: [''],
      customer: [null],
      tamerItem: [null],
      brand: [null],
      subBrand: [null],
    });
  }

  ngOnInit(): void {
    this.loadCustomerItems({ first: 0, rows: this.rows });
  }

  loadCustomerItems(event: TableLazyLoadEvent = {}): void {
    this.isLoading = true;
    const filters = this.filterForm.value;
    const page = event.first ? Math.floor(event.first / (event.rows || this.rows)) : 0;
    const size = event.rows || this.rows;

    const params: CustomerItemSearchParams = {
      page,
      size,
      customerItemNumber: filters.customerItemNumber || undefined,
      customerItemDescription: filters.customerItemDescription || undefined,
      'customer.customerNumber': filters.customer?.customerNumber || undefined,
      'tamerItem.erpItemNumber': filters.tamerItem?.erpItemNumber || undefined,
      'brand.brandName': filters.brand?.brandName || undefined,
      'subBrand.subBrandName': filters.subBrand?.subBrandName || undefined,
    };

    this.customerItemService.findAll(params).subscribe({
      next: (pageResult: Page<CustomerItem>) => {
        this.customerItems = pageResult.content || [];
        this.totalRecords = pageResult.totalElements || 0;
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load customer items.',
        });
        console.error(err);
      },
    });
  }

  onSearch(): void {
    this.loadCustomerItems({ first: 0, rows: this.rows });
  }

  onReset(): void {
    this.filterForm.reset();
    this.loadCustomerItems({ first: 0, rows: this.rows });
  }

  onNewCustomerItem(): void {
    this.router.navigate(
      ['.', { outlets: { canvasOutlet: ['customer-item', 'create'] } }],
      { relativeTo: this.route.parent?.parent }
    );
  }

  editCustomerItem(id: number): void {
    this.router.navigate([
      '/ai-studio/session/current',
      { outlets: { canvasOutlet: ['customer-item', 'edit', id] } },
    ]);
  }

  deleteCustomerItem(customerItem: CustomerItem): void {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this customer item?',
      header: 'Confirm Deletion',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes, Delete',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-success',
      accept: () => {
        this.customerItemService.deleteById(customerItem.customerItemId!).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Customer item deleted successfully.',
            });
            this.loadCustomerItems();
          },
          error: (err) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to delete customer item.',
            });
            console.error(err);
          },
        });
      },
    });
  }

  formatCustomer(customer?: CustomerPickerSummary | null): string {
    return customer ? `${customer.customerNumber} - ${customer.customerName}` : '-';
  }

  formatItem(item?: Item | null): string {
    return item ? `${item.erpItemNumber} - ${item.erpItemNameE}` : '-';
  }
}
